import React from 'react';
import { AlternativeSummary } from '../types';
import AlternativesSwitcher from './AlternativesSwitcher';
import RouteFocus from './RouteFocus';
import { alternativeMenuItems, AlternativeMenuHandlers } from './AlternativeJourneyChip';

// A trip's route alternatives, the same on the owner's Trip detail and the
// shared trip view (/s/<token>). Desktop (DetailSplitLayout, `split`): one
// full-width RouteFocus card per alternative, stacked, beside the page's map
// (the chips strip and the focus card said the same thing). Below the split:
// the chips strip, then the focused route's card with its map. `between`
// sits after the list or strip and before that card (the owner's generator
// and hints). The desktop side map is the page's: RouteFocusMap inside
// DetailSplitLayout. Convention: docs/codebase/ui-conventions.md ("Desktop
// split: map on the right").

export interface AlternativesViewProps {
  alternatives: AlternativeSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Hovered alternative: previewed on the map (and, below the split, on the focused card). */
  hoverPreviewId?: string | null;
  onHoverChange: (id: string | null) => void;
  /** Opens a route: its title, "See details", the menu's Details, a click on the selected card or chip. */
  onOpen: (id: string) => void;
  /** The ⋮ menu's other rows; a missing handler drops its row (a shared view passes only onCopy). */
  menu: Omit<AlternativeMenuHandlers, 'onOpen'>;
  /** The card's "Set as main" button. */
  onSetMain: (id: string) => void;
  settingMain?: boolean;
  /** The planner's "why this route" line, when there is one. */
  reasonFor?: (id: string) => string | null;
  /** False on read-only shared views: no "add prices" invite. */
  invitePricing?: boolean;
  /** Desktop layout (useDetailSplit). */
  split: boolean;
  between?: React.ReactNode;
}

const AlternativesView: React.FC<AlternativesViewProps> = ({
  alternatives, selectedId, onSelect, hoverPreviewId = null, onHoverChange, onOpen, menu,
  onSetMain, settingMain, reasonFor, invitePricing, split, between,
}) => {
  const selected = alternatives.find((a) => a.id === selectedId) ?? null;
  const hoverPreview = alternatives.find((a) => a.id === hoverPreviewId) ?? null;

  return (
    <>
      {split ? (
        // A click selects (the side map shows it), a click on the selected
        // card opens it, hover previews it on the map.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {alternatives.map((a) => (
            <RouteFocus
              key={a.id}
              alt={a}
              onSetMain={onSetMain}
              onSeeDetails={onOpen}
              settingMain={settingMain}
              reason={reasonFor?.(a.id) ?? null}
              invitePricing={invitePricing}
              row={{
                selected: a.id === selectedId,
                onSelect,
                onHoverChange,
                menuItems: alternativeMenuItems(a, {
                  ...menu,
                  onOpen,
                  onDeleteOthers: alternatives.length > 1 ? menu.onDeleteOthers : undefined,
                }),
              }}
            />
          ))}
        </div>
      ) : (
        <AlternativesSwitcher
          alternatives={alternatives}
          selectedId={selectedId}
          onSelect={onSelect}
          onOpen={onOpen}
          onHoverChange={onHoverChange}
          {...menu}
        />
      )}
      {between}
      {!split && selected && (
        <RouteFocus
          alt={selected}
          onSetMain={onSetMain}
          onSeeDetails={onOpen}
          settingMain={settingMain}
          previewAlt={hoverPreview}
          reason={reasonFor?.((hoverPreview ?? selected).id) ?? null}
          invitePricing={invitePricing}
        />
      )}
    </>
  );
};

export default AlternativesView;
